import React, { useState } from 'react';
import { Share2, Camera, ArrowLeft, X, AlertTriangle } from 'lucide-react';
import { shareOnMobile } from "react-mobile-share";
import { useTracker } from '../hooks/useAnalytics';

// Photo Detail Component
const PhotoDetail = ({ photo, navigateTo, setUserPhotos }) => {
  const [showCopyrightModal, setShowCopyrightModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'download' or 'share'
  const { trackDownload, trackCustom } = useTracker();

  if (!photo) {
    navigateTo('gallery');
    return null;
  }

  const urlToBase64 = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          console.log('Base64 result type:', typeof result);
          console.log('Base64 result preview:', result ? result.substring(0, 50) + '...' : 'null');
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Errore nella conversione URL to base64:', error);
      throw error;
    }
  };

  // Funzione per condividere con react-mobile-share
  const shareImage = async (photo) => {
    try {
      trackCustom('photo_share', {
        photoId: photo.id,
        photoYear: photo.year,
        platform: 'mobile_native'
      });
      const photoTitle = photo.title && photo.title.trim() !== '' ? photo.title : 'Foto storica Celtic Druid';
      const photoYear = photo.year || 'Anno sconosciuto';
      const photoImage = photo.fullSize || photo.fullSize;
      
      if (!photoImage) {
        throw new Error('Nessuna immagine disponibile');
      }
      
      console.log('Convertendo immagine in base64...');
      const base64Image = await urlToBase64(photoImage);
      console.log('Immagine convertita, tipo:', typeof base64Image);
      console.log('Preview base64:', base64Image ? base64Image.substring(0, 50) + '...' : 'null');
      
      shareOnMobile({
        text: `Scopri questa foto storica del Celtic Druid Irish Pub del ${photoYear}! 🍀`,
        url: "https://tinyurl.com/celticdruidstoria",
        title: `${photoTitle} - ${photoYear}`,
        images: [base64Image],
      });
    } catch (error) {
      console.error('Errore durante la condivisione:', error);
      // Fallback alla condivisione nativa del browser
      if (navigator.share) {
        const photoTitle = photo.title && photo.title.trim() !== '' ? photo.title : 'Foto storica Celtic Druid';
        const photoYear = photo.year || 'Anno sconosciuto';
        
        navigator.share({
          title: `${photoTitle} - ${photoYear}`,
          text: `Scopri questa foto storica del Celtic Druid Irish Pub del ${photoYear}! 🍀`,
          url: "https://celticdruid.com",
        })
        .then(() => console.log('Condivisione completata con successo'))
        .catch((error) => console.error('Errore durante la condivisione:', error));
      } else {
        alert('La condivisione non è supportata su questo dispositivo.');
      }
    }
  };

  // Funzione per aggiungere watermark e metadata copyright
  const addWatermarkAndDownload = async (imageUrl, fileName = 'logo192.jpg') => {
    try {
      // Crea un canvas per il watermark
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Carica l'immagine principale
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });
      
      // Imposta le dimensioni del canvas
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Disegna l'immagine principale
      ctx.drawImage(img, 0, 0);
      
      // Carica il logo per il watermark
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        logo.onload = resolve;
        logo.onerror = () => {
          console.warn('Logo non trovato, procedo senza watermark');
          resolve(); // Continua anche senza logo
        };
        logo.src = '/logo_header.svg';
      });
      
      // Aggiungi il watermark se il logo è stato caricato
      if (logo.complete && logo.naturalWidth > 0) {
        const watermarkSize = Math.min(canvas.width, canvas.height) * 0.15; // 15% della dimensione minima
        const margin = 20;
        const x = canvas.width - watermarkSize - margin;
        const y = canvas.height - watermarkSize - margin;
        
        // Aggiungi una leggera ombra per il watermark
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 5;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
        
        // Disegna il logo con trasparenza
        ctx.globalAlpha = 0.7;
        ctx.drawImage(logo, x, y, 90, 30);
        
        // Reset ombra per il testo
        ctx.shadowColor = 'transparent';
        
        // Testo "storyarch.io" sotto il logo del watermark
        ctx.font = "12px Arial, sans-serif";
        ctx.textAlign = "center";
        
        // Posizione del testo (centrato sotto il logo del watermark)
        const textX = x + 45; // Centro del logo (90/2 = 45)
        const textY = y + 30 + 15; // Sotto il logo (altezza 30 + margine 15)
        
        // Sfumatura nera (ombra)
        ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
        ctx.fillText("storyarch.io", textX + 1, textY + 1);
        
        // Testo bianco sopra
        ctx.fillStyle = "white";
        ctx.fillText("storyarch.io", textX, textY);
        
        // Reset trasparenza
        ctx.globalAlpha = 1.0;
      }
      
      // Converti in blob con metadata copyright
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error('Errore nella creazione del blob');
        }
        
        // Crea l'URL per il download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        
        // Aggiungi attributi per i metadata (se supportati dal browser)
        link.setAttribute('data-copyright', '© Celtic Druid Irish Pub - Tutti i diritti riservati');
        link.setAttribute('data-license', 'Uso personale non commerciale');
        link.setAttribute('data-source', 'Celtic Druid Irish Pub');
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 'image/jpeg', 0.9);
      
    } catch (error) {
      console.error('Errore durante la creazione del watermark:', error);
      // Fallback al download normale
      downloadImageFallback(imageUrl, fileName);
    }
  };

  // Funzione di fallback per il download senza watermark
  const downloadImageFallback = (imageUrl, fileName) => {
    if (imageUrl.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    
    fetch(imageUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(error => {
        console.error('Errore durante il download dell\'immagine:', error);
        alert('Non è stato possibile scaricare l\'immagine. Riprova più tardi.');
      });
  };

  // Gestione del click su download
  const handleDownloadClick = () => {
    setPendingAction('download');
    setShowCopyrightModal(true);
  };

  // Gestione del click su condividi
  const handleShareClick = () => {
    setPendingAction('share');
    setShowCopyrightModal(true);
  };

  // Conferma del copyright e esecuzione dell'azione
  const handleCopyrightConfirm = () => {
    setShowCopyrightModal(false);
    
    if (pendingAction === 'download') {
      trackDownload(photo);
      addWatermarkAndDownload(photo.fullSize, `celtic-druid-${photo.title.replace(/\s+/g, '-').toLowerCase()}.jpg`);
    } else if (pendingAction === 'share') {
      shareImage(photo);
    }
    
    setPendingAction(null);
  };

  // Annulla l'azione
  const handleCopyrightCancel = () => {
    setShowCopyrightModal(false);
    setPendingAction(null);
  };

  const handleCreateMemory = () => {
    navigateTo('beforeAfter');
  };

  const handleGoBack = () => {
    navigateTo('gallery');
  };

  return (
    <>
      <div className="container mx-auto px-4 py-6 text-amber-100">
        <div className="flex items-center mb-4">
          <button 
            onClick={handleGoBack}
            className="flex items-center gap-2 text-amber-300 hover:text-amber-200 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Torna alla galleria</span>
          </button>
        </div>

        <div className="bg-stone-800 rounded-lg overflow-hidden shadow-md">
          <img 
            src={photo.fullSize} 
            alt={photo.title} 
            className="w-full object-cover max-h-80" 
          />
          
          <div className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-bold text-amber-300">{photo.title}</h2>
              <span className="bg-amber-500 text-stone-900 px-2 py-1 rounded text-sm font-bold">
                {photo.year}
              </span>
            </div>
            
            <p className="mb-4">{photo.description}</p>
            
            <div className="flex justify-between gap-3 mb-4">
              <button 
                onClick={handleDownloadClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#44403c',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Salva
              </button>
              
              <button 
                onClick={handleShareClick}
                className="flex items-center justify-center gap-2 py-2 px-4 bg-stone-700 rounded-lg text-sm"
              >
                <Share2 size={16} />
                Condividi
              </button>
            </div>
            
            <button 
              onClick={handleCreateMemory}
              className="w-full py-3 bg-green-700 hover:bg-green-600 text-white rounded-lg shadow transition-colors flex items-center justify-center"
            >
              <Camera size={20} className="mr-2" />
              Crea il tuo ricordo
            </button>
          </div>
        </div>
      </div>

      {/* Modal Copyright */}
      {showCopyrightModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-stone-800 rounded-lg max-w-md w-full p-6 text-amber-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle size={24} className="text-amber-500" />
                <h3 className="text-lg font-bold text-amber-300">Avviso Copyright</h3>
              </div>
              <button 
                onClick={handleCopyrightCancel}
                className="text-amber-300 hover:text-amber-200"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="mb-3 text-sm">
                Questa foto è di proprietà del <strong>Celtic Druid Irish Pub</strong>.
              </p>
              <p className="mb-3 text-sm">
                È consentito l'uso per <strong>scopi personali</strong>, ma è vietata la distribuzione o l'utilizzo per <strong>fini commerciali</strong> senza autorizzazione scritta.
              </p>
              <p className="text-xs text-amber-300">
                © Celtic Druid Irish Pub - Tutti i diritti riservati
              </p>
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={handleCopyrightCancel}
                className="flex-1 py-2 px-4 bg-stone-700 hover:bg-stone-600 rounded-lg text-sm transition-colors"
              >
                Annulla
              </button>
              <button 
                onClick={handleCopyrightConfirm}
                className="flex-1 py-2 px-4 bg-amber-600 hover:bg-amber-700 text-stone-900 rounded-lg text-sm font-medium transition-colors"
              >
                Accetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoDetail;